import { GenericField, InputProps } from "../Fields/GenericField";
import { useField } from "formik";
import { formatDate } from "@/lib/Formatters/formatDate";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { cn } from "@/lib/utils";
import { CalendarIcon, Cross2Icon } from "@radix-ui/react-icons";
import { Calendar, CalendarProps } from "@/components/ui/calendar";
import dayjs from "dayjs";
import { Button } from "@/components/catalyst/button";
import { useCallback, useMemo } from "react";

export type DatePickerCalendarProps = Omit<
  Partial<CalendarProps>,
  "mode" | "selected"
>;

export type DateInputProps = InputProps & {
  min?: string;
  max?: string;
  onChange?: (val: string | null) => void;
  calendarProps?: DatePickerCalendarProps;
  clearable?: boolean;
};

export function DateInput({
  min,
  max,
  onChange,
  calendarProps,
  clearable = false,
  autoFocus,
  ...props
}: DateInputProps) {
  const { emptyValue = null } = props;
  const [field, meta, { setValue }] = useField<string | null>(props.name);
  const { value } = field;
  const showError = !!meta.error && meta.touched;

  // Memoize formatted date to prevent unnecessary recalculations
  const formattedDate = useMemo(() => formatDate(value), [value]);

  // Memoize button className to prevent recalculation on every render
  const buttonClassName = useMemo(
    () =>
      cn(
        "shadow-xs w-full justify-start border-gray-300 text-left text-sm font-normal transition-shadow duration-100",
        !value && "text-gray-500",
        {
          "focus:outline-hidden border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500":
            showError,
          "focus:border-blue-500 focus:ring-blue-500": !showError,
        },
      ),
    [value, showError],
  );

  // Memoize default month to prevent new Date creation on every render
  const defaultMonth = useMemo(() => new Date(value || Date.now()), [value]);

  // Memoize selected date to prevent new Date creation on every render
  const selectedDate = useMemo(
    () => (value ? new Date(value) : undefined),
    [value],
  );

  // Memoize min/max dayjs objects to prevent recreation on every disabled check
  const minDay = useMemo(() => (min ? dayjs(min) : null), [min]);
  const maxDay = useMemo(() => (max ? dayjs(max) : null), [max]);

  // Memoize disabled function to prevent recreation on every render
  const isDateDisabled = useCallback(
    (date: Date) => {
      const day = dayjs(date);

      if (minDay && minDay.isAfter(day)) {
        return true;
      }

      return !!(maxDay && maxDay.isBefore(day));
    },
    [minDay, maxDay],
  );

  // Memoize select handler to prevent recreation
  const handleDateSelect = useCallback(
    async (date: Date | undefined, close: () => void) => {
      if (date) {
        const dateString = dayjs(date).format("YYYY-MM-DD");
        await setValue(dateString);
        onChange?.(dateString);
        close(); // Close the popover after selection
      } else {
        await setValue(emptyValue);
        onChange?.(emptyValue);
        close();
      }
    },
    [setValue, onChange, emptyValue],
  );

  // Memoize clear handler to prevent recreation
  const handleClear = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      await setValue(null);
      onChange?.(null);
    },
    [setValue, onChange],
  );

  // Memoize view node to prevent recreation
  const viewNode = useMemo(() => <span>{formattedDate}</span>, [formattedDate]);

  return (
    <GenericField viewNode={viewNode} {...props}>
      {() => (
        <Popover className="relative">
          <PopoverButton
            as={Button}
            outline
            className={buttonClassName}
            autoFocus={autoFocus}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? <span>{formattedDate}</span> : <span>Pick a date</span>}
            {clearable && value ? (
              <Cross2Icon
                className="ml-auto h-4 w-4 text-gray-400 hover:text-gray-600"
                onClick={handleClear}
              />
            ) : null}
          </PopoverButton>

          <PopoverPanel
            className="absolute z-10 mt-1 w-auto rounded-md border border-gray-200 bg-white p-0 shadow-lg focus:outline-none"
            anchor="bottom start"
          >
            {({ close }) => (
              <Calendar
                mode="single"
                defaultMonth={defaultMonth}
                disabled={isDateDisabled}
                selected={selectedDate}
                onSelect={(date) => handleDateSelect(date, close)}
                autoFocus
                {...calendarProps}
              />
            )}
          </PopoverPanel>
        </Popover>
      )}
    </GenericField>
  );
}
